import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSession } from "@/lib/auth/auth";
import { useTenant } from "@/providers/session";
import ErrorIcon from "@mui/icons-material/Error";
import * as Portal from "@radix-ui/react-portal";
import * as Sentry from "@sentry/react";
import { CircleArrowUp, File, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { LuCheckCircle2 } from "react-icons/lu";
import {
  type InvoiceUploadStatus,
  clearLocalStorage,
  retrieveObjectsFromLocal,
} from "../../persistent-storage/localStorage";

import { cn } from "@/lib/utils";

export enum UploadStatus {
  UPLOADED = "uploaded",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
  SENT = "sent",
}

export interface FileNotification {
  id: string;
  name: string;
  status: UploadStatus;
  invoiceId?: string;
}

const UploadProgressBox = (props: { refetch?: Function }) => {
  const [notificationItems, setNotificationItems] = useState<any[]>([]);
  const [value, setValue] = useState<string>("");
  const { data: session } = useSession();
  const { tenantId, companySlug } = useTenant();
  const [requiresOcrReview, setRequiresOcrReview] = React.useState(false);

  const onClose = () => {
    clearLocalStorage(`invoiceUploadStatus-${tenantId}`);
  };

  const allUploadsCompleted = notificationItems.every(
    (item) => item.status === UploadStatus.COMPLETED,
  );

  useEffect(() => {
    const handleUnload = () => {
      if (
        notificationItems.some((item) => item.status !== UploadStatus.COMPLETED)
      ) {
        Sentry.captureMessage("User closed tab during file upload", {
          extra: {
            notificationItems: notificationItems,
          },
        });
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (
        notificationItems.some((item) => item.status !== UploadStatus.COMPLETED)
      ) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [notificationItems]);

  React.useEffect(() => {
    setRequiresOcrReview((session as any)?.user?.requiresOcrReview);
  }, [session?.user]);

  const countCompleted = (notifications: InvoiceUploadStatus[]) => {
    return notifications.filter(
      (notification) => notification.status == UploadStatus.COMPLETED,
    ).length;
  };
  const countSent = (notifications: InvoiceUploadStatus[]) => {
    return notifications.length;
  };

  useEffect(() => {
    const notificationsItems = retrieveObjectsFromLocal(
      `invoiceUploadStatus-${tenantId}`,
    );

    setNotificationItems(notificationsItems);

    // Define the event listener function
    const handleStorageChange = () => {
      const notificationsItems = retrieveObjectsFromLocal(
        `invoiceUploadStatus-${tenantId}`,
      );
      setNotificationItems(notificationsItems);
    };

    // Add the event listener
    document.addEventListener(
      "invoiceChangedLocalStorage",
      handleStorageChange,
    );

    // Add the event listener
    document.addEventListener("tenantChanged", handleStorageChange);

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener(
        "invoiceChangedLocalStorage",
        handleStorageChange,
      );

      document.removeEventListener("tenantChanged", handleStorageChange);
    };
  }, [tenantId]);

  useEffect(() => {
    if (notificationItems.length > 0) {
      setValue("uploads");
    }
  }, [notificationItems]);

  if (notificationItems.length === 0) {
    return null;
  }

  const notificationIcon = (notification: any) => {
    switch (notification.status) {
      case UploadStatus.SENT:
        return <CircleArrowUp size={20} color="grey" />;
      case UploadStatus.UPLOADED:
        return <CircleArrowUp size={20} color="grey" />;
      case UploadStatus.PROCESSING:
        return <CircleArrowUp size={20} color="grey" />;
      case UploadStatus.FAILED:
        return <ErrorIcon color="error" />;
      case UploadStatus.COMPLETED:
        return <LuCheckCircle2 color="#27A544" size={20} />;

      default:
        return null;
    }
  };

  const handleOnValueChange = (value: string) => {
    setValue(value);
  };

  return (
    <Portal.Root container={document.getElementById("status-cards")}>
      <Accordion
        type="single"
        collapsible
        value={value}
        onValueChange={handleOnValueChange}
        disabled={notificationItems.length === 0}
      >
        <AccordionItem value="uploads" className="border-none">
          <Card
            className={cn(
              "shadow-lg overflow-hidden",
              allUploadsCompleted ? "bg-green-500" : "bg-blue-500",
            )}
            style={{
              width: "450px",
            }}
          >
            <div className="flex flex-1 items-center justify-between py-4 px-4">
              <div className="flex-1">
                <AccordionTrigger className="p-0 w-full [&[data-state=open]]:no-underline hover:no-underline">
                  <CardHeader
                    className={cn(
                      "p-1 w-full",
                      allUploadsCompleted ? "bg-green-500" : "bg-blue-500",
                    )}
                  >
                    <CardTitle className="w-full text-start text-white">
                      {allUploadsCompleted ? (
                        <>
                          {`Hochladen erfolgreich! (${countSent(
                            notificationItems,
                          )}/${countSent(notificationItems)})`}
                        </>
                      ) : (
                        `${countCompleted(notificationItems)} von ${countSent(
                          notificationItems,
                        )} werden hochgeladen`
                      )}
                    </CardTitle>
                  </CardHeader>
                </AccordionTrigger>
              </div>
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: no comments */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="hover:bg-gray-100 p-1 rounded-full ml-2"
              >
                <X size={16} />
              </div>
            </div>
            <AccordionContent className="pb-0 max-h-[200px] overflow-y-auto">
              <CardContent className="px-4 py-3 space-y-4 bg-background">
                {notificationItems.map((notification) => (
                  <div key={notification.uploadTimestamp}>
                    <div className="flex items-center space-x-2 w-full">
                      <div className="flex space-x-2 flex-1 ">
                        <File size={20} className="shrink-0" />
                        <span className="font-medium w-[350px] text-ellipsis overflow-hidden whitespace-nowrap">
                          {notification.fileName}
                        </span>
                      </div>
                      <div>{notificationIcon(notification)}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>
      </Accordion>
    </Portal.Root>
  );
};

export default UploadProgressBox;
